import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../atoms/button";
import Loading from "../atoms/loading";
import BotOpsLiveShell from "./botOpsLiveShell";
import { ChatBubble } from "./flowChatBubbles";
import { buildLiveBubbles, buildTimelineFromResults } from "./flowTimelineView";
import { isNearBottom } from "./botOpsSandboxChat";
import FlowChatEditor from "./flowChatEditor";
import FlowTimedReplay from "./flowTimedReplay";
import FlowScenarioLibrary from "./flowScenarioLibrary";
import TheaterChrome from "./theaterChrome";
import CompareSetup from "./CompareSetup";
import CompareTheater from "./CompareTheater";
import { computeDiscrepancies } from "./compareDiscrepancies";
import {
    getPromptfooSuite,
    putPromptfooSuite,
    getPromptfooAsserts,
    getPromptfooModels,
    listPromptfooRuns,
    postPromptfooRun,
    getPromptfooRun,
    refreshPromptfooRun,
    getSandboxTrace,
} from "./functions";
import { mergeTraceEvents, traceEventsFromTimeline } from "./flowReplayTrace";

const DEFAULT_PROVIDER_OPTS = { temperature: 0, max_tokens: 1200, seed: 42 };
const ACTIVE_STATUSES = ["queued", "running"];
const POLL_MS = 5000;
const POLL_MS_FLOW = 1500;

function newCaseId() {
    return `c${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function newTurnId() {
    return `t${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function emptyCase() {
    return {
        id: newCaseId(),
        description: "",
        mensagem: "",
        assertIds: [],
        vars: {},
    };
}

function emptyTurn() {
    return {
        id: newTurnId(),
        mensagem: "",
        assertIds: [],
        assertParams: {},
    };
}

function getCaseKind(c) {
    return c?.kind === "flow" ? "flow" : "promptfoo";
}

function normalizeRuns(data) {
    if (Array.isArray(data)) return data;
    if (data?.runs && Array.isArray(data.runs)) return data.runs;
    return [];
}

function shortId(id) {
    return id ? String(id).slice(0, 8) : "—";
}

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("pt-BR");
    } catch {
        return String(value);
    }
}

function providerLabel(providers, providerId) {
    const match = providers.find((p) => p.id === providerId);
    if (match?.label) return match.label;
    if (!providerId) return "—";
    return providerId.replace(/^openrouter:/, "").split("/").pop() || providerId;
}

function formatSummary(summary) {
    if (!summary || typeof summary !== "object") return "—";
    return Object.entries(summary)
        .map(([pid, s]) => {
            const label = pid.replace(/^openrouter:/, "").split("/").pop() || pid;
            const pass = s?.pass ?? 0;
            const fail = s?.fail ?? 0;
            return `${label}: ${pass}/${pass + fail}`;
        })
        .join(" · ");
}

function splitRunResults(run) {
    const results = Array.isArray(run?.results) ? run.results : [];
    return {
        flowResults: results.filter((r) => r.turnId),
        promptfooResults: results.filter((r) => !r.turnId),
    };
}

function snapshotHasFlow(snapshot) {
    const cases = snapshot?.cases;
    if (!Array.isArray(cases)) return false;
    return cases.some((c) => getCaseKind(c) === "flow");
}

function isCompareRun(run) {
    if (run?.kind === "compare") return true;
    // Fallback: POST/poll parcial às vezes chega sem kind antes do save completo.
    return Array.isArray(run?.models) && run.models.length >= 2;
}

function runNeedsFastPoll(run) {
    if (isCompareRun(run)) return true;
    if (run?.progress) return true;
    return snapshotHasFlow(run?.suite_snapshot);
}

function resolveViewModeForRun(detail) {
    if (!shouldEnterTheater(detail)) return "suite";
    return isCompareRun(detail) ? "compare" : "theater";
}

function shortModelLabel(id, label) {
    if (label) return label;
    if (!id) return "?";
    const s = String(id).replace(/^openrouter:/, "");
    const parts = s.split("/");
    return parts[parts.length - 1] || s;
}

function formatCompareRunSummary(run) {
    const models = Array.isArray(run?.models) ? run.models : [];
    const labels = models
        .map((m) => shortModelLabel(m.id, m.label))
        .filter(Boolean)
        .join(" · ");
    const discrepancies = Array.isArray(run?.discrepancies)
        ? run.discrepancies
        : computeDiscrepancies(run?.results || [], models);
    const discCount = discrepancies.length;
    const parts = [];
    if (labels) parts.push(`compare: ${labels}`);
    else parts.push("compare");
    parts.push(`${discCount} disc`);
    return parts.join(" · ");
}

function formatRunSummary(run) {
    if (isCompareRun(run)) return formatCompareRunSummary(run);
    return formatSummary(run.summary);
}

export function resolveTheaterTitle({ selectedRunDetail, cases, selectedFlowCaseId }) {
    const snapCases = selectedRunDetail?.suite_snapshot?.cases;
    const results = Array.isArray(selectedRunDetail?.results) ? selectedRunDetail.results : [];
    const resultCaseId = results.find((row) => row?.caseId)?.caseId || null;
    const snapshotFirstCaseId = Array.isArray(snapCases) ? snapCases[0]?.id : null;
    const suiteFirstCaseId = Array.isArray(cases) ? cases[0]?.id : null;
    const caseId =
        selectedRunDetail?.progress?.caseId ||
        resultCaseId ||
        snapshotFirstCaseId ||
        selectedFlowCaseId ||
        suiteFirstCaseId ||
        null;
    const fromSnap = Array.isArray(snapCases)
        ? snapCases.find((c) => c.id === caseId)
        : null;
    const fromSuite = Array.isArray(cases) ? cases.find((c) => c.id === caseId) : null;
    const meta = fromSnap || fromSuite;
    if (meta?.description) return meta.description;
    if (caseId) return String(caseId);
    if (selectedRunDetail?.id) return `Run ${String(selectedRunDetail.id).slice(0, 8)}`;
    return "Execução";
}

export function shouldEnterTheater(detail) {
    return Boolean(detail && detail.type !== "error");
}

function isFlowRunDetail(run, currentFlowCases = [], flowResults = []) {
    if (!run) return false;
    if (run.progress) return true;
    if (snapshotHasFlow(run.suite_snapshot)) return true;
    if (Array.isArray(flowResults) && flowResults.length > 0) return true;
    if (ACTIVE_STATUSES.includes(run.status) && currentFlowCases.length > 0) return true;
    return false;
}

function getPollMs(runs) {
    const active = runs.filter((r) => ACTIVE_STATUSES.includes(r.status));
    if (active.some(runNeedsFastPoll)) return POLL_MS_FLOW;
    return POLL_MS;
}

/**
 * Merge de refresh no poll live — poll atrasado não pode encolher timeline/results
 * nem apagar progress ainda válido.
 */
function mergeRunPollUpdate(prev, data) {
    if (!data || data.type === "error") return prev;
    if (!prev) return data;

    const merged = {
        ...prev,
        ...data,
        kind: data.kind || prev.kind,
        models: data.models?.length ? data.models : prev.models,
    };

    const prevTimeline = Array.isArray(prev.timeline) ? prev.timeline : [];
    const nextTimeline = Array.isArray(data.timeline) ? data.timeline : [];
    if (prevTimeline.length > nextTimeline.length) {
        merged.timeline = prevTimeline;
    }

    const prevResults = Array.isArray(prev.results) ? prev.results : [];
    const nextResults = Array.isArray(data.results) ? data.results : [];
    if (prevResults.length > nextResults.length) {
        merged.results = prevResults;
    }

    if (
        data.progress == null &&
        prev.progress != null &&
        ACTIVE_STATUSES.includes(String(data.status || prev.status || ""))
    ) {
        merged.progress = prev.progress;
    }

    return merged;
}

/** Fases em que o turno corrente ainda não entrou em `results[]`. */
const LIVE_PENDING_PHASES = new Set(["enviando", "aguardando_resposta", "avaliando"]);

function flowRowKey(row) {
    return `${row.caseId}:${row.turnId}`;
}

/**
 * Chat ao vivo: balões de `results` + turno pendente a partir de `progress`
 * (mensagem do user e “bot digitando” antes do assert fechar o turno).
 */
function FlowLiveChat({ results = [], cases = [], progress = null, timeline = [] }) {
    const threadRef = useRef(null);
    const stickRef = useRef(true);
    const safeResults = Array.isArray(results) ? results : [];
    const timelineBubbles = Array.isArray(timeline) && timeline.length > 0
        ? buildLiveBubbles({ timeline, results: safeResults, progress })
        : null;
    const caseById = new Map();
    for (const c of cases || []) {
        if (c?.id) caseById.set(c.id, c);
    }

    const groups = [];
    for (const row of safeResults) {
        const last = groups[groups.length - 1];
        if (!last || last.caseId !== row.caseId) {
            groups.push({ caseId: row.caseId, rows: [row] });
        } else {
            last.rows.push(row);
        }
    }

    const phase = progress?.phase ? String(progress.phase) : "";
    const turnIndex = Number(progress?.turnIndex) || 0;
    const progressCaseId = progress?.caseId || null;
    const completedForCase = progressCaseId
        ? safeResults.filter((r) => r.caseId === progressCaseId).length
        : safeResults.length;
    const pendingMessage = String(progress?.message || "").trim();
    const showPendingTurn =
        Boolean(progress) &&
        LIVE_PENDING_PHASES.has(phase) &&
        turnIndex > completedForCase;
    const showUserPending = showPendingTurn && (Boolean(pendingMessage) || phase === "enviando");
    const showBotPending =
        showPendingTurn &&
        (phase === "aguardando_resposta" ||
            phase === "avaliando" ||
            (phase === "enviando" && Boolean(pendingMessage)));
    const showPreparando =
        Boolean(progress) &&
        phase === "preparando" &&
        safeResults.length === 0 &&
        !showPendingTurn;

    const pendingCaseId = progressCaseId || groups[groups.length - 1]?.caseId || null;
    const pendingMeta = pendingCaseId ? caseById.get(pendingCaseId) : null;
    const pendingTitle = pendingMeta?.description || pendingCaseId || "Cenário";

    const hasTimelineBubbles = Boolean(timelineBubbles?.length);
    const hasCompleted = groups.length > 0;
    const emptyIdle =
        !hasCompleted && !showPendingTurn && !showPreparando && !hasTimelineBubbles;

    useEffect(() => {
        const el = threadRef.current;
        if (!el) return undefined;
        const onScroll = () => {
            stickRef.current = isNearBottom(el);
        };
        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const el = threadRef.current;
        if (!el || !stickRef.current) return undefined;
        if (typeof el.scrollTo === "function") {
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
            el.scrollTop = el.scrollHeight;
        }
        return undefined;
    }, [results, progress?.turnIndex, progress?.phase, timeline]);

    return (
        <div
            ref={threadRef}
            className="max-h-[70vh] space-y-3 overflow-y-auto"
            data-testid="live-chat-thread"
            aria-label="Live chat flow"
        >
            {emptyIdle ? (
                <p className="text-sm text-gray-400">Aguardando primeiros turnos…</p>
            ) : null}
            {showPreparando ? (
                <p className="text-sm text-gray-400" data-testid="live-preparando">
                    Preparando conversa sandbox…
                </p>
            ) : null}
            {hasTimelineBubbles
                ? timelineBubbles.map((bubble, index) => (
                      <ChatBubble
                          key={`${bubble.role}-${bubble.turnId || index}-${index}`}
                          role={bubble.role}
                          text={bubble.text}
                          pass={bubble.pass}
                          testId={
                              bubble.role === "pending"
                                  ? "live-pending-bot"
                                  : `live-${bubble.role}-${bubble.turnId || index}`
                          }
                      />
                  ))
                : null}
            {!hasTimelineBubbles && groups.map((group) => {
                const meta = caseById.get(group.caseId);
                const title = meta?.description || group.caseId || "Cenário";
                return (
                    <div key={group.caseId} className="mb-4">
                        <h5 className="text-xs font-medium text-laranjinha mb-2">{title}</h5>
                        {group.rows.map((row) => (
                            <div key={flowRowKey(row)}>
                                <ChatBubble
                                    role="user"
                                    text={row.input}
                                    testId={`live-user-${row.turnId}`}
                                />
                                <ChatBubble
                                    role="bot"
                                    text={row.output}
                                    pass={row.pass}
                                    testId={`live-bot-${row.turnId}`}
                                />
                            </div>
                        ))}
                    </div>
                );
            })}
            {!hasTimelineBubbles && showPendingTurn ? (
                <div className="mb-4" data-testid="live-pending-turn">
                    {!hasCompleted ||
                    (pendingCaseId && groups[groups.length - 1]?.caseId !== pendingCaseId) ? (
                        <h5 className="text-xs font-medium text-laranjinha mb-2">{pendingTitle}</h5>
                    ) : null}
                    {showUserPending ? (
                        <ChatBubble
                            role="user"
                            text={pendingMessage || "Enviando…"}
                            testId="live-pending-user"
                        />
                    ) : null}
                    {showBotPending ? (
                        <ChatBubble
                            role="pending"
                            text={phase === "avaliando" ? "avaliando asserts…" : "digitando…"}
                            testId="live-pending-bot"
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export {
    getPollMs,
    mergeRunPollUpdate,
    FlowLiveChat,
    isCompareRun,
    formatCompareRunSummary,
    resolveViewModeForRun,
};

function buildMatrix(run, resultsSubset) {
    const snapshot = run?.suite_snapshot || {};
    const cases = Array.isArray(snapshot.cases) ? snapshot.cases : [];
    const providers = Array.isArray(snapshot.providers) ? snapshot.providers : [];
    const results = resultsSubset ?? (Array.isArray(run?.results) ? run.results : []);

    const caseIds =
        cases.length > 0
            ? cases.map((c) => c.id)
            : [...new Set(results.map((r) => r.caseId).filter(Boolean))];
    const providerIds =
        providers.length > 0
            ? providers.map((p) => p.id)
            : [...new Set(results.map((r) => r.providerId).filter(Boolean))];

    const resultMap = new Map();
    for (const row of results) {
        resultMap.set(`${row.caseId}::${row.providerId}`, row);
    }

    return { cases, providers, caseIds, providerIds, resultMap };
}

function ResultCell({ row }) {
    const hasDetail =
        row.input ||
        row.output ||
        row.latencyMs != null ||
        row.intent ||
        row.handoff_now != null;

    if (!hasDetail) {
        return (
            <>
                <span className={row.pass ? "text-green-400" : "text-red-400"}>
                    {row.pass ? "pass" : "fail"}
                </span>
                {row.reason ? (
                    <span className="block text-gray-400 mt-0.5" title={row.reason}>
                        {row.reason}
                    </span>
                ) : null}
            </>
        );
    }

    return (
        <details className="group">
            <summary className="cursor-pointer list-none">
                <span className={row.pass ? "text-green-400" : "text-red-400"}>
                    {row.pass ? "pass" : "fail"}
                </span>
                {row.reason ? (
                    <span className="block text-gray-400 mt-0.5" title={row.reason}>
                        {row.reason}
                    </span>
                ) : null}
            </summary>
            <div className="mt-2 space-y-1 text-gray-300 border-t border-gray-700 pt-2">
                {row.input ? (
                    <p>
                        <span className="text-gray-500">input:</span> {row.input}
                    </p>
                ) : null}
                {row.output ? (
                    <pre className="whitespace-pre-wrap break-words text-gray-400 bg-gray-950/50 p-1.5 rounded">
                        {row.output}
                    </pre>
                ) : null}
                {row.latencyMs != null ? (
                    <p>
                        <span className="text-gray-500">latência:</span> {row.latencyMs}ms
                    </p>
                ) : null}
                {row.intent ? (
                    <p>
                        <span className="text-gray-500">intent:</span> {row.intent}
                    </p>
                ) : null}
                {row.handoff_now != null ? (
                    <p>
                        <span className="text-gray-500">handoff_now:</span>{" "}
                        {String(row.handoff_now)}
                    </p>
                ) : null}
            </div>
        </details>
    );
}

const BotOpsTestesPanel = ({ token, goodToast, badToast }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [cases, setCases] = useState([]);
    const [providers, setProviders] = useState([]);
    const [assertCatalog, setAssertCatalog] = useState([]);
    const [recommendedModels, setRecommendedModels] = useState([]);
    const [runs, setRuns] = useState([]);
    const [manualModel, setManualModel] = useState("");
    const [selectedRunId, setSelectedRunId] = useState(null);
    const [selectedRunDetail, setSelectedRunDetail] = useState(null);
    const [testesTab, setTestesTab] = useState("conversas");
    const [selectedFlowCaseId, setSelectedFlowCaseId] = useState(null);
    const [viewMode, setViewMode] = useState("suite");
    const [compareSetupOpen, setCompareSetupOpen] = useState(false);
    const [comparing, setComparing] = useState(false);
    const [replayUntilTs, setReplayUntilTs] = useState(null);
    const [replayTraceEvents, setReplayTraceEvents] = useState([]);
    const theaterRootRef = useRef(null);
    const runsRef = useRef(runs);
    const selectedRunIdRef = useRef(selectedRunId);
    runsRef.current = runs;
    selectedRunIdRef.current = selectedRunId;

    useEffect(() => {
        setReplayUntilTs(null);
    }, [selectedRunDetail?.id]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!selectedRunDetail || ACTIVE_STATUSES.includes(selectedRunDetail.status)) {
                setReplayTraceEvents([]);
                return;
            }
            const timeline = selectedRunDetail.timeline || [];
            const fromTimeline = traceEventsFromTimeline(timeline);
            const convId = selectedRunDetail.progress?.conversation_id ?? null;
            let cw = [];
            if (convId != null && token) {
                const data = await getSandboxTrace(token, convId, null);
                if (!cancelled && data && data.type !== "error" && data.type !== "unavailable") {
                    cw = Array.isArray(data.events) ? data.events : [];
                }
            }
            if (!cancelled) setReplayTraceEvents(mergeTraceEvents(cw, fromTimeline));
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [selectedRunDetail, token]);

    const loadData = useCallback(async () => {
        setLoading(true);
        const [suiteData, assertsData, modelsData, runsData] = await Promise.all([
            getPromptfooSuite(token),
            getPromptfooAsserts(token),
            getPromptfooModels(token),
            listPromptfooRuns(token),
        ]);

        if (suiteData?.type === "error") {
            badToast(suiteData.message || "Erro ao carregar suíte");
        } else {
            setCases(Array.isArray(suiteData?.cases) ? suiteData.cases : []);
            setProviders(Array.isArray(suiteData?.providers) ? suiteData.providers : []);
        }

        if (assertsData?.type === "error") {
            badToast(assertsData.message || "Erro ao carregar asserts");
        } else if (Array.isArray(assertsData)) {
            setAssertCatalog(assertsData);
        }

        if (modelsData?.type === "error") {
            badToast(modelsData.message || "Erro ao carregar modelos");
        } else if (Array.isArray(modelsData)) {
            setRecommendedModels(modelsData);
        }

        if (runsData?.type === "error") {
            badToast(runsData.message || "Erro ao carregar execuções");
        } else {
            setRuns(normalizeRuns(runsData));
        }

        setLoading(false);
    }, [token, badToast]);

    useEffect(() => {
        if (token) loadData();
    }, [token, loadData]);

    useEffect(() => {
        const flowCases = cases.filter((c) => getCaseKind(c) === "flow");
        if (flowCases.length === 0) {
            setSelectedFlowCaseId(null);
            return;
        }
        setSelectedFlowCaseId((prev) => {
            if (prev && flowCases.some((c) => c.id === prev)) return prev;
            return flowCases[0].id;
        });
    }, [cases]);

    const hasActiveRun = runs.some((r) => ACTIVE_STATUSES.includes(r.status));
    // Quando a run ganha progress/suite flow, o intervalo muda (5s → 1.5s).
    // Precisa reiniciar o effect — senão o timer antigo fica em 5s e a trilha live “congela”.
    const activePollMs = hasActiveRun ? getPollMs(runs) : null;

    useEffect(() => {
        if (activePollMs == null || !token) return undefined;

        let cancelled = false;
        let timer = null;

        const schedule = (ms) => {
            if (cancelled) return;
            timer = setTimeout(tick, ms);
        };

        const tick = async () => {
            const currentRuns = runsRef.current;
            const active = currentRuns.filter((r) => ACTIVE_STATUSES.includes(r.status));
            if (active.length === 0) return;

            for (const run of active) {
                if (cancelled) return;
                const latest = runsRef.current.find((r) => r.id === run.id) || run;
                if (!ACTIVE_STATUSES.includes(latest.status)) continue;

                const data = await refreshPromptfooRun(token, latest.id);
                if (cancelled) return;
                if (data?.type === "error") continue;

                setRuns((prev) =>
                    prev.map((r) => (r.id === latest.id ? mergeRunPollUpdate(r, data) : r))
                );
                if (selectedRunIdRef.current === latest.id) {
                    setSelectedRunDetail((prev) => mergeRunPollUpdate(prev, data));
                    if (isCompareRun(data) || isCompareRun(latest)) {
                        setViewMode("compare");
                    }
                }
            }

            const nextMs = getPollMs(runsRef.current);
            if (runsRef.current.some((r) => ACTIVE_STATUSES.includes(r.status))) {
                schedule(nextMs);
            }
        };

        // Primeiro ciclo após o intervalo (como setInterval) — o detalhe inicial
        // já vem do getPromptfooRun ao abrir a run.
        schedule(activePollMs);
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [activePollMs, token]);

    useEffect(() => {
        if (viewMode !== "theater" && viewMode !== "compare") return undefined;
        const el = theaterRootRef.current;
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ block: "start", behavior: "smooth" });
        }
        return undefined;
    }, [viewMode, selectedRunId]);

    const isProviderSelected = (modelId) => providers.some((p) => p.id === modelId);

    const toggleRecommendedProvider = (model) => {
        if (isProviderSelected(model.id)) {
            setProviders((prev) => prev.filter((p) => p.id !== model.id));
        } else {
            setProviders((prev) => [
                ...prev,
                { id: model.id, label: model.label, ...DEFAULT_PROVIDER_OPTS },
            ]);
        }
    };

    const addManualProvider = () => {
        const id = manualModel.trim();
        if (!id) return;
        if (!id.startsWith("openrouter:")) {
            badToast("Modelo manual deve começar com openrouter:");
            return;
        }
        if (isProviderSelected(id)) {
            badToast("Modelo já adicionado");
            return;
        }
        const label = id.replace(/^openrouter:/, "").split("/").pop() || id;
        setProviders((prev) => [...prev, { id, label, ...DEFAULT_PROVIDER_OPTS }]);
        setManualModel("");
    };

    const removeProvider = (providerId) => {
        setProviders((prev) => prev.filter((p) => p.id !== providerId));
    };

    const updateCase = (index, field, value) => {
        setCases((prev) =>
            prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
        );
    };

    const toggleCaseAssert = (index, assertId) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== index) return c;
                const ids = Array.isArray(c.assertIds) ? [...c.assertIds] : [];
                const pos = ids.indexOf(assertId);
                if (pos >= 0) ids.splice(pos, 1);
                else ids.push(assertId);
                return { ...c, assertIds: ids };
            })
        );
    };

    const addCase = () => setCases((prev) => [...prev, emptyCase()]);

    const removeCase = (index) => {
        setCases((prev) => prev.filter((_, i) => i !== index));
    };

    const updateTurn = (caseIndex, turnIndex, field, value) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const turns = Array.isArray(c.turns) ? [...c.turns] : [];
                turns[turnIndex] = { ...turns[turnIndex], [field]: value };
                return { ...c, turns };
            })
        );
    };

    const toggleTurnAssert = (caseIndex, turnIndex, assertId) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const turns = Array.isArray(c.turns) ? [...c.turns] : [];
                const turn = { ...turns[turnIndex] };
                const ids = Array.isArray(turn.assertIds) ? [...turn.assertIds] : [];
                const pos = ids.indexOf(assertId);
                if (pos >= 0) ids.splice(pos, 1);
                else ids.push(assertId);
                turn.assertIds = ids;
                turns[turnIndex] = turn;
                return { ...c, turns };
            })
        );
    };

    const updateTurnAssertParam = (caseIndex, turnIndex, key, value) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const turns = Array.isArray(c.turns) ? [...c.turns] : [];
                const turn = { ...turns[turnIndex] };
                const params = { ...(turn.assertParams || {}) };
                if (value === "" || value == null) {
                    delete params[key];
                } else {
                    params[key] = value;
                }
                turn.assertParams = params;
                turns[turnIndex] = turn;
                return { ...c, turns };
            })
        );
    };

    const addTurn = (caseIndex) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const turns = Array.isArray(c.turns)
                    ? [...c.turns, emptyTurn()]
                    : [emptyTurn()];
                return { ...c, turns };
            })
        );
    };

    const removeTurn = (caseIndex, turnIndex) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const current = Array.isArray(c.turns) ? c.turns : [];
                if (current.length <= 1) return c;
                const turns = current.filter((_, ti) => ti !== turnIndex);
                return { ...c, turns };
            })
        );
    };

    const moveTurn = (caseIndex, turnIndex, direction) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const turns = [...(Array.isArray(c.turns) ? c.turns : [])];
                const target = turnIndex + direction;
                if (target < 0 || target >= turns.length) return c;
                const [item] = turns.splice(turnIndex, 1);
                turns.splice(target, 0, item);
                return { ...c, turns };
            })
        );
    };

    const toggleTurnBurst = (caseIndex, turnIndex) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const turns = Array.isArray(c.turns) ? [...c.turns] : [];
                const turn = { ...turns[turnIndex] };
                turn.burst = !turn.burst;
                turns[turnIndex] = turn;
                return { ...c, turns };
            })
        );
    };

    const addDebouncePreset = (caseIndex) => {
        setCases((prev) =>
            prev.map((c, i) => {
                if (i !== caseIndex) return c;
                const preset = [
                    {
                        id: newTurnId(),
                        mensagem: "mensagem burst 1",
                        burst: true,
                        assertIds: [],
                        assertParams: {},
                    },
                    {
                        id: newTurnId(),
                        mensagem: "mensagem burst 2",
                        burst: true,
                        assertIds: [],
                        assertParams: {},
                    },
                    {
                        id: newTurnId(),
                        mensagem: "mensagem burst 3",
                        burst: true,
                        assertIds: ["assertDebounceGrouped"],
                        assertParams: {},
                    },
                ];
                const turns = Array.isArray(c.turns) ? [...c.turns, ...preset] : preset;
                return { ...c, kind: "flow", turns };
            })
        );
    };

    const updateCaseTags = (caseIndex, tags) => {
        updateCase(caseIndex, "tags", tags);
    };

    const addFlowScenario = () => {
        const id = newCaseId();
        setCases((prev) => [
            ...prev,
            {
                id,
                kind: "flow",
                description: "Novo cenário",
                tags: [],
                turns: [emptyTurn()],
            },
        ]);
        setSelectedFlowCaseId(id);
        setTestesTab("conversas");
    };

    const removeFlowCaseById = (caseId) => {
        setCases((prev) => prev.filter((c) => c.id !== caseId));
    };

    const handleSave = async () => {
        setSaving(true);
        const result = await putPromptfooSuite(token, { cases, providers });
        setSaving(false);
        if (result?.type === "error") {
            badToast(result.message || "Erro ao salvar suíte");
            return;
        }
        goodToast("Suíte salva!");
    };

    const handleExecute = async () => {
        // Cenário selecionado na aba Conversas → só aquele caso (evita estourar
        // o orçamento Vercel rodando os 4 flows exemplo de uma vez).
        const runBody =
            testesTab === "conversas" && selectedFlowCaseId
                ? { caseIds: [selectedFlowCaseId] }
                : {};
        if (testesTab === "conversas" && !selectedFlowCaseId) {
            const flowCount = cases.filter((c) => getCaseKind(c) === "flow").length;
            if (flowCount > 1) {
                badToast(
                    "Selecione um cenário na lista (Executar a suíte inteira estoura o tempo do servidor)"
                );
                return;
            }
        }

        setExecuting(true);
        const result = await postPromptfooRun(token, runBody);
        setExecuting(false);

        if (result?.type === "error") {
            if (result.status === 409) {
                badToast("Já existe um teste em andamento");
            } else {
                badToast(result.message || "Erro ao iniciar execução");
            }
            return;
        }

        goodToast(
            runBody.caseIds
                ? `Execução iniciada (${runBody.caseIds[0]})`
                : "Execução iniciada"
        );
        const runsData = await listPromptfooRuns(token);
        if (runsData?.type !== "error") {
            setRuns(normalizeRuns(runsData));
        }
        if (result?.id) {
            setSelectedRunId(result.id);
            const detail = await getPromptfooRun(token, result.id);
            if (detail?.type !== "error") {
                setSelectedRunDetail(detail);
                setViewMode(resolveViewModeForRun(detail));
                setRuns((prev) => {
                    const exists = prev.some((r) => r.id === result.id);
                    if (exists) {
                        return prev.map((r) =>
                            r.id === result.id ? { ...r, ...detail } : r
                        );
                    }
                    return [detail, ...prev];
                });
            }
        }
    };

    const handleCompareConfirm = async ({ models }) => {
        if (!selectedFlowCaseId || !Array.isArray(models) || models.length < 2) return;

        setComparing(true);
        const result = await postPromptfooRun(token, {
            kind: "compare",
            caseIds: [selectedFlowCaseId],
            models,
        });
        setComparing(false);
        setCompareSetupOpen(false);

        if (result?.type === "error") {
            if (result.status === 409) {
                badToast("Já existe um teste em andamento");
            } else {
                badToast(result.message || "Erro ao iniciar comparação");
            }
            return;
        }

        goodToast(`Comparação iniciada (${models.length} modelos lado a lado)`);
        const modelsWithSlots = models.map((m, slot) => ({
            slot,
            id: m.id,
            temperatura: m.temperatura ?? null,
        }));
        // Entra no teatro compare imediatamente — não depende do GET ter kind.
        const optimistic = {
            id: result.id,
            status: result.status || "running",
            kind: "compare",
            models: modelsWithSlots,
            results: [],
            discrepancies: [],
            progress: {
                phase: "preparando",
                turnIndex: 0,
                turnTotal: 0,
                slots: modelsWithSlots.map((m) => ({
                    slot: m.slot,
                    conversation_id: null,
                    phase: "preparando",
                    status: "running",
                })),
            },
            suite_snapshot: {
                cases: cases.filter((c) => c.id === selectedFlowCaseId),
            },
        };
        setSelectedRunId(result.id);
        setSelectedRunDetail(optimistic);
        setViewMode("compare");
        setRuns((prev) => {
            const exists = prev.some((r) => r.id === result.id);
            if (exists) {
                return prev.map((r) => (r.id === result.id ? { ...r, ...optimistic } : r));
            }
            return [optimistic, ...prev];
        });

        const detail = await getPromptfooRun(token, result.id);
        if (detail?.type !== "error") {
            const merged = {
                ...detail,
                kind: detail.kind || "compare",
                models: detail.models?.length ? detail.models : modelsWithSlots,
            };
            setSelectedRunDetail(merged);
            setViewMode("compare");
            setRuns((prev) =>
                prev.map((r) => (r.id === result.id ? { ...r, ...merged } : r))
            );
        }
        const runsData = await listPromptfooRuns(token);
        if (runsData?.type !== "error") {
            setRuns(normalizeRuns(runsData));
        }
    };

    const handleSelectRun = async (runId) => {
        setSelectedRunId(runId);
        const detail = await getPromptfooRun(token, runId);
        if (detail?.type === "error") {
            badToast(detail.message || "Erro ao carregar execução");
            return;
        }
        setSelectedRunDetail(detail);
        setViewMode(resolveViewModeForRun(detail));
        setRuns((prev) =>
            prev.map((r) => (r.id === runId ? { ...r, ...detail } : r))
        );
    };

    const handleBackToSuite = () => {
        setViewMode("suite");
        setCompareSetupOpen(false);
    };

    if (loading) {
        return <Loading />;
    }

    const { flowResults, promptfooResults } = selectedRunDetail
        ? splitRunResults(selectedRunDetail)
        : { flowResults: [], promptfooResults: [] };
    const matrix =
        selectedRunDetail && promptfooResults.length > 0
            ? buildMatrix(selectedRunDetail, promptfooResults)
            : null;

    const flowCases = cases.filter((c) => getCaseKind(c) === "flow");
    const promptfooCaseEntries = cases
        .map((c, index) => ({ c, index }))
        .filter(({ c }) => getCaseKind(c) === "promptfoo");
    const selectedFlowCaseIndex = cases.findIndex((c) => c.id === selectedFlowCaseId);
    const selectedFlowCase =
        selectedFlowCaseIndex >= 0 ? cases[selectedFlowCaseIndex] : null;
    const effectiveTab =
        testesTab === "conversas" && flowCases.length === 0 ? "matriz" : testesTab;

    const renderSelectedRunBody = () => {
        if (!selectedRunDetail) return null;

        const status = selectedRunDetail.status;
        const progress = selectedRunDetail.progress;
        const flowCasesSnapshot =
            selectedRunDetail.suite_snapshot?.cases || matrix?.cases || [];
        const isFlowRun = isFlowRunDetail(selectedRunDetail, flowCases, flowResults);
        const isFlowLive = ACTIVE_STATUSES.includes(status) && isFlowRun;
        const isFlowReplay = (status === "completed" || status === "failed") && isFlowRun;

        if (
            promptfooResults.length === 0 &&
            flowResults.length === 0 &&
            !isFlowLive &&
            !isFlowReplay
        ) {
            return <p className="text-sm text-gray-400">Resultados ainda não disponíveis.</p>;
        }

        return (
            <>
                {isFlowLive || isFlowReplay ? (
                    <BotOpsLiveShell
                        mode={isFlowLive ? "live" : "replay"}
                        conversationId={
                            progress?.conversation_id ??
                            selectedRunDetail.progress?.conversation_id ??
                            null
                        }
                        locked={false}
                        progress={null}
                        token={token}
                        isActive={isFlowLive}
                        onToastBad={badToast}
                        traceReplay={
                            isFlowReplay
                                ? { events: replayTraceEvents, untilTs: replayUntilTs }
                                : null
                        }
                        childrenChat={
                            isFlowLive ? (
                                <FlowLiveChat
                                    results={flowResults}
                                    cases={flowCasesSnapshot}
                                    progress={progress}
                                    timeline={selectedRunDetail.timeline}
                                />
                            ) : (
                                <FlowTimedReplay
                                    key={selectedRunDetail.id}
                                    timeline={
                                        selectedRunDetail.timeline?.length > 0
                                            ? selectedRunDetail.timeline
                                            : buildTimelineFromResults(flowResults)
                                    }
                                    results={flowResults}
                                    cases={flowCasesSnapshot}
                                    onClockChange={({ untilIso }) => setReplayUntilTs(untilIso)}
                                />
                            )
                        }
                    />
                ) : null}
                {matrix &&
                matrix.caseIds.length > 0 &&
                matrix.providerIds.length > 0 &&
                promptfooResults.length > 0 ? (
                    <div
                        className={`overflow-x-auto ${
                            isFlowLive || isFlowReplay ? "mt-4" : ""
                        }`}
                    >
                        <table className="w-full text-xs text-left">
                            <thead>
                                <tr className="text-gray-400 border-b border-gray-700">
                                    <th className="py-2 pr-3">Caso</th>
                                    {matrix.providerIds.map((pid) => (
                                        <th key={pid} className="py-2 pr-3">
                                            {providerLabel(matrix.providers, pid)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.caseIds.map((caseId) => {
                                    const caseMeta = matrix.cases.find((c) => c.id === caseId);
                                    const label = caseMeta?.description || caseId || "—";
                                    return (
                                        <tr key={caseId} className="border-b border-gray-800">
                                            <td className="py-2 pr-3 text-gray-300">{label}</td>
                                            {matrix.providerIds.map((pid) => {
                                                const row = matrix.resultMap.get(
                                                    `${caseId}::${pid}`
                                                );
                                                if (!row) {
                                                    return (
                                                        <td
                                                            key={pid}
                                                            className="py-2 pr-3 text-gray-500"
                                                        >
                                                            —
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td key={pid} className="py-2 pr-3">
                                                        <ResultCell row={row} />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}
            </>
        );
    };

    if (viewMode === "compare" && selectedRunDetail && isCompareRun(selectedRunDetail)) {
        const isCompareLive = ACTIVE_STATUSES.includes(selectedRunDetail.status);
        return (
            <section
                className="rounded-lg border border-[#d59106] p-4 mb-4"
                aria-label="Testes"
            >
                <div ref={theaterRootRef} data-testid="compare-theater-root">
                    {selectedRunDetail.error ? (
                        <p className="text-sm text-red-300 mb-3">{selectedRunDetail.error}</p>
                    ) : null}
                    <CompareTheater
                        run={selectedRunDetail}
                        title={resolveTheaterTitle({
                            selectedRunDetail,
                            cases,
                            selectedFlowCaseId,
                        })}
                        onBack={handleBackToSuite}
                        isLive={isCompareLive}
                    />
                </div>
            </section>
        );
    }

    if (viewMode === "theater" && selectedRunDetail) {
        return (
            <section
                className="rounded-lg border border-[#d59106] p-4 mb-4"
                aria-label="Testes"
            >
                <div ref={theaterRootRef} data-testid="theater-root">
                    <TheaterChrome
                        title={resolveTheaterTitle({
                            selectedRunDetail,
                            cases,
                            selectedFlowCaseId,
                        })}
                        status={selectedRunDetail.status}
                        progress={
                            ACTIVE_STATUSES.includes(selectedRunDetail.status)
                                ? selectedRunDetail.progress
                                : null
                        }
                        onBack={handleBackToSuite}
                    />
                    <div
                        className="rounded-lg border border-[#b47c06]/40 bg-gray-900/60 p-3"
                        aria-label="Detalhe da execução"
                    >
                        {selectedRunDetail.error ? (
                            <p className="text-sm text-red-300 mb-3">
                                {selectedRunDetail.error}
                            </p>
                        ) : null}
                        {renderSelectedRunBody()}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            className="rounded-lg border border-[#d59106] p-4 mb-4"
            aria-label="Testes"
        >
            <h2 className="text-lg text-laranjinha mb-4">Testes</h2>
            <p className="text-xs text-gray-300 mb-4">
                Cenários flow em conversa (balões + replay) e matriz promptfoo na outra aba.
            </p>

            <div role="tablist" className="flex gap-2 mb-4">
                <button
                    type="button"
                    role="tab"
                    aria-selected={effectiveTab === "conversas"}
                    onClick={() => setTestesTab("conversas")}
                    className={`px-3 py-1.5 rounded text-sm border ${
                        effectiveTab === "conversas"
                            ? "border-laranjinha text-laranjinha bg-gray-800"
                            : "border-gray-700 text-gray-300"
                    }`}
                >
                    Conversas
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={effectiveTab === "matriz"}
                    onClick={() => setTestesTab("matriz")}
                    className={`px-3 py-1.5 rounded text-sm border ${
                        effectiveTab === "matriz"
                            ? "border-laranjinha text-laranjinha bg-gray-800"
                            : "border-gray-700 text-gray-300"
                    }`}
                >
                    Matriz promptfoo
                </button>
            </div>

            {effectiveTab === "conversas" ? (
                <div className="mb-6 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-sm font-medium text-white">Cenários flow</h3>
                        <Button
                            click={addFlowScenario}
                            classes="bg-gray-700 hover:bg-gray-600 px-3 py-1 text-white"
                        >
                            Adicionar cenário flow
                        </Button>
                    </div>
                    <FlowScenarioLibrary
                        flowCases={flowCases}
                        selectedCaseId={selectedFlowCaseId}
                        onSelect={setSelectedFlowCaseId}
                        onRemove={removeFlowCaseById}
                    />
                    {selectedFlowCase && selectedFlowCaseIndex >= 0 ? (
                        <div className="rounded-lg border border-[#b47c06]/40 bg-gray-900/60 p-3 space-y-3">
                            <input
                                type="text"
                                value={selectedFlowCase.description || ""}
                                onChange={(e) =>
                                    updateCase(
                                        selectedFlowCaseIndex,
                                        "description",
                                        e.target.value
                                    )
                                }
                                placeholder="Descrição do cenário"
                                className="w-full text-black p-2 rounded text-sm"
                                aria-label="Descrição do cenário"
                            />
                            <FlowChatEditor
                                caseItem={selectedFlowCase}
                                caseIndex={selectedFlowCaseIndex}
                                asserts={assertCatalog}
                                onUpdateTurn={updateTurn}
                                onToggleAssert={toggleTurnAssert}
                                onUpdateAssertParam={updateTurnAssertParam}
                                onAddTurn={addTurn}
                                onRemoveTurn={removeTurn}
                                onMoveTurn={moveTurn}
                                onToggleBurst={toggleTurnBurst}
                                onAddDebouncePreset={addDebouncePreset}
                                onUpdateTags={updateCaseTags}
                            />
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
                                <Button
                                    click={() => setCompareSetupOpen(true)}
                                    classes="bg-[#b47c06] hover:bg-[#d59106] px-3 py-1.5 text-white text-sm"
                                >
                                    Comparar modelos
                                </Button>
                            </div>
                            <CompareSetup
                                open={compareSetupOpen}
                                token={token}
                                caseDescription={selectedFlowCase.description}
                                confirming={comparing}
                                onClose={() => setCompareSetupOpen(false)}
                                onConfirm={handleCompareConfirm}
                            />
                        </div>
                    ) : null}
                </div>
            ) : (
                <>
                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-white mb-2">Modelos (providers)</h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {recommendedModels.map((model) => {
                                const selected = isProviderSelected(model.id);
                                return (
                                    <label
                                        key={model.id}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer border transition-colors ${
                                            selected
                                                ? "bg-[#d59106]/20 border-[#d59106] text-laranjinha"
                                                : "bg-gray-800/60 border-gray-600 text-gray-300 hover:border-gray-500"
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={() => toggleRecommendedProvider(model)}
                                            className="h-4 w-4 accent-[#d59106]"
                                        />
                                        <span>{model.label}</span>
                                    </label>
                                );
                            })}
                        </div>

                        {providers
                            .filter((p) => !recommendedModels.some((m) => m.id === p.id))
                            .map((p) => (
                                <div
                                    key={p.id}
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-gray-800 text-gray-200 border border-[#b47c06]/40 mr-2 mb-2"
                                >
                                    <span>{p.label || p.id}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeProvider(p.id)}
                                        className="text-red-400 hover:text-red-300"
                                        aria-label={`Remover ${p.label || p.id}`}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}

                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            <input
                                type="text"
                                value={manualModel}
                                onChange={(e) => setManualModel(e.target.value)}
                                placeholder="openrouter:provider/modelo"
                                className="flex-1 min-w-[200px] text-black p-2 rounded text-sm"
                                aria-label="Modelo manual"
                            />
                            <Button
                                click={addManualProvider}
                                classes="bg-[#d59106] hover:bg-[#b47c06] px-3 py-1"
                            >
                                Adicionar modelo
                            </Button>
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <h3 className="text-sm font-medium text-white">Casos promptfoo</h3>
                            <Button
                                click={addCase}
                                classes="bg-gray-700 hover:bg-gray-600 px-3 py-1 text-white"
                            >
                                Adicionar caso
                            </Button>
                        </div>

                        {promptfooCaseEntries.length === 0 ? (
                            <p className="text-sm text-gray-400">Nenhum caso promptfoo.</p>
                        ) : null}

                        <div className="space-y-4">
                            {promptfooCaseEntries.map(({ c, index }) => (
                                <div
                                    key={c.id || index}
                                    className="rounded-lg border border-[#b47c06]/40 bg-gray-900/60 p-3 space-y-3"
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            type="text"
                                            value={c.description || ""}
                                            onChange={(e) =>
                                                updateCase(index, "description", e.target.value)
                                            }
                                            placeholder="Descrição do caso"
                                            className="flex-1 min-w-[160px] text-black p-2 rounded text-sm"
                                            aria-label={`Descrição caso ${index + 1}`}
                                        />
                                        <Button
                                            click={() => removeCase(index)}
                                            classes="bg-red-500 hover:bg-red-600 px-3 py-1"
                                        >
                                            Remover
                                        </Button>
                                    </div>
                                    <textarea
                                        value={c.mensagem || ""}
                                        onChange={(e) =>
                                            updateCase(index, "mensagem", e.target.value)
                                        }
                                        rows={3}
                                        placeholder="Mensagem do usuário simulada..."
                                        className="w-full text-black p-2 rounded text-sm"
                                        aria-label={`Mensagem caso ${index + 1}`}
                                    />
                                    <div>
                                        <p className="text-xs text-gray-300 mb-2">Asserts</p>
                                        <div className="flex flex-wrap gap-2">
                                            {assertCatalog.map((assert) => {
                                                const checked = (c.assertIds || []).includes(
                                                    assert.id
                                                );
                                                return (
                                                    <label
                                                        key={assert.id}
                                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer border ${
                                                            checked
                                                                ? "bg-[#d59106]/20 border-[#d59106] text-laranjinha"
                                                                : "bg-gray-800/60 border-gray-600 text-gray-300"
                                                        }`}
                                                        title={assert.description}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() =>
                                                                toggleCaseAssert(index, assert.id)
                                                            }
                                                            className="h-3 w-3 accent-[#d59106]"
                                                        />
                                                        {assert.label}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            <div className="mb-6 flex flex-wrap gap-3">
                <Button
                    click={handleSave}
                    disabled={saving}
                    classes="bg-[#d59106] hover:bg-[#b47c06] px-4 py-2 font-medium"
                >
                    {saving ? "Salvando…" : "Salvar suíte"}
                </Button>
                <Button
                    click={handleExecute}
                    disabled={executing || comparing || compareSetupOpen}
                    classes="bg-green-700 hover:bg-green-600 px-4 py-2 font-medium text-white disabled:opacity-40"
                    title={
                        compareSetupOpen
                            ? "Feche ou confirme a comparação lado a lado — Executar roda um único modelo"
                            : undefined
                    }
                >
                    {executing
                        ? "Iniciando…"
                        : testesTab === "conversas" && selectedFlowCaseId
                          ? "Executar cenário (1 modelo)"
                          : "Executar"}
                </Button>
            </div>

            <div className="border-t border-[#b47c06]/30 pt-4">
                <h3 className="text-sm font-medium text-white mb-2">Execuções recentes</h3>
                {runs.length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhuma execução registrada.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead>
                                <tr className="text-gray-400 border-b border-gray-700">
                                    <th className="py-2 pr-3">ID</th>
                                    <th className="py-2 pr-3">Status</th>
                                    <th className="py-2 pr-3">Resumo</th>
                                    <th className="py-2">Criado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((run) => (
                                    <tr
                                        key={run.id}
                                        onClick={() => handleSelectRun(run.id)}
                                        className={`border-b border-gray-800 cursor-pointer hover:bg-gray-800/50 ${
                                            selectedRunId === run.id ? "bg-gray-800/70" : ""
                                        }`}
                                    >
                                        <td className="py-2 pr-3 text-laranjinha font-mono">
                                            {shortId(run.id)}
                                        </td>
                                        <td className="py-2 pr-3 text-gray-300">
                                            {run.status || "—"}
                                        </td>
                                        <td
                                            className="py-2 pr-3 text-gray-400 text-xs"
                                            data-testid={`run-summary-${shortId(run.id)}`}
                                        >
                                            {formatRunSummary(run)}
                                        </td>
                                        <td className="py-2 text-gray-400 text-xs">
                                            {formatDate(run.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            </div>
        </section>
    );
};

export default BotOpsTestesPanel;
